import { useState, useEffect } from 'react';
import imageCompression from 'browser-image-compression';
import { Link, useParams } from 'react-router-dom';
import { AiOutlineArrowLeft } from 'react-icons/ai';
import countryData from "./countryData";
import apiClient from "../utils/apiClient";
import { userProfile} from "../utils/APIRoutes";
import Logout from "./Logout";
import { client } from '../sanity/sanityClient';
import { v4 as uuidv4 } from 'uuid';
import './ProductForm.css';

// The Currency options for East Africa and major global currencies
const currencyOptions = [
  { code: 'TZS', name: 'Tanzanian Shilling (TZS)' },
  { code: 'KES', name: 'Kenyan Shilling (KES)' },
  { code: 'UGX', name: 'Ugandan Shilling (UGX)' },
  { code: 'RWF', name: 'Rwandan Franc (RWF)' },
  { code: 'USD', name: 'US Dollar (USD)' },
  { code: 'ZAR', name: 'South African Rand (ZAR)' },
  { code: 'NGN', name: 'Nigerian Naira (NGN)' },
  { code: 'EUR', name: 'Euro (EUR)' }
];

const ProductForm = () => {
  const { userId } = useParams();
  const [productName, setProductName] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('TZS');
  const [details, setDetails] = useState('');
  const [location, setLocation] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [images, setImages] = useState([]);
  const [optimizedImages, setOptimizedImages] = useState([]);
  const [categories] = useState([
    'clothingandfashion', 'electronics', 'kidsandtoys', 'kitchen', 'fashion', 'useditems', 'cuisines',
    'grocery', 'vehicles', 'hotels', 'sports', 'livestock', 'crops', 'agritech', 'giftoptions', 'truck2cargo','booksandstationery',
    'foodandbeverages', 'homeandfurnitures', 'arts', 'medical', 'beauty', 'servicesandinstitutions', 'eventsandnightclubs',
    'jewelryandaccessories', 'salon', 'hardware', 'estate', 'construction', 'hospitality', 'energyandfuel',
  ]);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [categoryId, setCategoryId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [countrySearch, setCountrySearch] = useState('');
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [criteriaLoading, setCriteriaLoading] = useState(true);
  const [criteriaError, setCriteriaError] = useState('');

    useEffect(() => {
      const checkUserCriteria = async () => {
        try {
          const userData = localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY);

          if (!userData) {
            setIsAllowed(false);
            setHasActiveSubscription(false);
            setCriteriaLoading(false);
            return;
          }

          const parsedUser = JSON.parse(userData);

          // Only the account owner should be looking at their own form
          if (parsedUser._id !== userId) {
            setIsAllowed(false);
            setHasActiveSubscription(false);
            setCriteriaLoading(false);
            return;
          }

          // Fetch fresh profile rather than trusting cached localStorage —
          // `allowed` can be revoked and a subscription can lapse without
          // the client ever refreshing its cached user object.
          const { data } = await apiClient.get(`${userProfile}/${userId}`);
          const profile = data.profile;

          const activeSub =
            !!profile.subscription?.active &&
            (!profile.subscription?.endDate ||
              new Date(profile.subscription.endDate) > new Date());

          setIsAllowed(!!profile.allowed);
          setHasActiveSubscription(activeSub);

        } catch (error) {
          console.error("Error checking user criteria:", error);
          setCriteriaError("Unable to verify your account status. Please try again.");
          setIsAllowed(false);
          setHasActiveSubscription(false);
        } finally {
          setCriteriaLoading(false);
        }
      };

      checkUserCriteria();
    }, [userId]);

    const meetsCriteria = isAllowed && hasActiveSubscription;

  // Filter countries based on search term
  useEffect(() => {
    if (countrySearch.trim() === '') {
      setFilteredCountries([]);
      return;
    }

    const results = countryData.filter(country =>
      country.name.toLowerCase().includes(countrySearch.toLowerCase())
    ).slice(0, 10);

    setFilteredCountries(results);
  }, [countrySearch]);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country.name);
    setCountrySearch(country.name);
    setShowCountryDropdown(false);
  };

  // Fetch Geolocation on Component Mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
        },
        (error) => {
          console.error("Error fetching geolocation:", error.message);
          setErrorMessage("Unable to fetch geolocation. Please enable location services.");
        }
      );
    } else {
      setErrorMessage("Geolocation is not supported by your browser.");
    }
  }, []);

  useEffect(() => {
    const fetchCategoryId = async () => {
      if (selectedCategory) {
        try {
          const result = await client.fetch(
            `*[_type == $selectedCategory][0]{ _id }`,
            { selectedCategory }
          );

          if (result) {
            setCategoryId(result._id);
          } else {
            setErrorMessage(`Category "${selectedCategory}" not found.`);
          }
        } catch (error) {
          setErrorMessage('Error fetching category ID');
          console.error('Error fetching category ID:', error.message);
        }
      }
    };

    fetchCategoryId();
  }, [selectedCategory]);

  const validateForm = () => {
    const errors = {};

    if (!productName.trim()) errors.productName = 'Product name is required';
    if (!price) errors.price = 'Price is required';
    if (!details.trim()) errors.details = 'Details are required';
    if (!location.trim()) errors.location = 'Location is required';
    if (!selectedCategory) errors.selectedCategory = 'Category is required';
    if (optimizedImages.length === 0) errors.images = 'At least one image is required';
    if (!latitude || !longitude) errors.geolocation = 'Geolocation is required';
    if (!selectedCountry) errors.country = 'Country is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);

    if (files.length === 0) {
      setFormErrors({ ...formErrors, images: 'Please select at least one image' });
      return;
    }

    const compressAndConvertToWebP = async (file) => {
      // Only compress images larger than 0.5MB
      const shouldCompress = file.size > 0.5 * 1024 * 1024;

      if (!shouldCompress) {
        return file;
      }

      const options = {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        fileType: 'image/webp',
      };

      try {
        const compressedFile = await imageCompression(file, options);
        return compressedFile;
      } catch (error) {
        console.error('Error during image compression:', error);
        throw new Error('Image compression failed');
      }
    };

    try {
      const optimizedFiles = await Promise.all(
        files.map((file) => compressAndConvertToWebP(file))
      );

      setImages(files);
      setOptimizedImages(optimizedFiles);
      setFormErrors({ ...formErrors, images: '' });

    } catch (error) {
      console.error('Image processing failed, please exclude video:', error);
      setErrorMessage('Failed to process images, please exclude video');
      setFormErrors({
        ...formErrors,
        images: 'Failed to process images, please exclude video'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    if (!categoryId) {
      setErrorMessage('Select a valid category before adding a product.');
      setIsLoading(false);
      return;
    }

    try {
      const imageIds = await uploadImages(optimizedImages);
      const slugValue = await generateSlug(productName, selectedCategory);

      const newProduct = await client.create({
        _type: selectedCategory,
        name: productName,
        slug: { _type: 'slug', current: slugValue },
        price: Number(price),
        currency,
        country: selectedCountry,
        details,
        user: userId,
        location,
        geolocation: {
          _type: 'geopoint',
          lat: latitude,
          lng: longitude,
        },
        image: imageIds.map((id) => ({
          _type: 'image',
          _key: uuidv4(),
          asset: { _type: 'reference', _ref: id },
        })),
      });

      setSuccessMessage('Product added successfully!');
      resetForm();
      console.log('Product created:', newProduct);

    } catch (error) {
      setErrorMessage('Failed to add the product. Please try again.');
      console.error('Error adding product:', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadImages = async (images) => {
    const imageIds = [];

    for (let image of images) {
      try {
        const asset = await client.assets.upload('image', image, {
          filename: image.name
        });

        imageIds.push(asset._id);

      } catch (error) {
        console.error('Image upload failed:', error.message);
        throw new Error('Image upload failed');
      }
    }

    return imageIds;
  };

  const resetForm = () => {
    setProductName('');
    setPrice('');
    setCurrency('TZS');
    setDetails('');
    setLocation('');
    setSelectedCountry('');
    setCountrySearch('');
    setImages([]);
    setOptimizedImages([]);
    setSelectedCategory('');
    setCategoryId(null);
    setFormErrors({});
  };

  const generateSlug = async (name, category) => {
    // 1. Create base slug
    let baseSlug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');

    // 2. Add category to improve uniqueness
    const categorySlug = category.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    let slug = `${baseSlug}-${categorySlug}`;

    // 3. Check for existing slugs and increment counter if needed
    try {
      const query = `count(*[_type == $category && slug.current match $slug + "*"])`;
      const params = { category, slug };
      const count = await client.fetch(query, params);

      if (count > 0) {
        slug = `${slug}-${count + 1}`;
      }

    } catch (error) {
      console.error('Error checking slug uniqueness:', error);
      slug = `${slug}-${Math.random().toString(36).substring(2, 6)}`;
    }

    return slug;
  };

  const handleDetailsChange = (e) => {
    const value = e.target.value;

    const phoneNumberRegex = /(\+\d{1,3}\s?)?(\(\d{1,4}\)|\d{1,4})[\s-]?\d{1,4}[\s-]?\d{1,9}/g;

    if (phoneNumberRegex.test(value)) {
      setFormErrors({
        ...formErrors,
        details: 'Phone numbers are not allowed in product details'
      });
    } else {
      setDetails(value);
      setFormErrors({
        ...formErrors,
        details: ''
      });
    }
  };

  // Show loading while checking criteria
  if (criteriaLoading) {
    return (
      <div className="product-form-container">
        <Link to={'/'}>
          <AiOutlineArrowLeft
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              fontSize: 24,
              cursor: 'pointer'
            }}
          />
        </Link>

        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Checking your account status...</p>
        </div>
      </div>
    );
  }

  // Show error message if user doesn't meet criteria
      if (criteriaLoading) {
      return (
        <div className="product-form-container">
          <Link to={'/'}>
            <AiOutlineArrowLeft style={{ position: 'absolute', top: 8, left: 8, fontSize: 24, cursor: 'pointer' }} />
          </Link>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Checking your account status...</p>
          </div>
        </div>
      );
    }

    if (!meetsCriteria) {
      return (
        <div className="product-form-container">
          <Link to={'/'}>
            <AiOutlineArrowLeft style={{ position: 'absolute', top: 8, left: 8, fontSize: 24, cursor: 'pointer' }} />
          </Link>

          <div style={{ textAlign: 'center', padding: '2rem' }}>
            {criteriaError ? (
              <>
                <h2 style={{ color: 'red' }}>Something Went Wrong</h2>
                <p>{criteriaError}</p>
              </>
            ) : !isAllowed && !hasActiveSubscription ? (
              <>
                <h2 style={{ color: 'red' }}>Access Denied</h2>
                <p>You haven't been approved to sell yet, and you don't have an active subscription.</p>
                <p>
                  Please <Link to={`/userrequests/${userId}`}>request to sell</Link> and, once approved,
                  start a trial or subscribe to post products.
                </p>
              </>
            ) : !isAllowed ? (
              <>
                <h2 style={{ color: 'red' }}>Access Denied</h2>
                <p>You don't meet criteria to post products on this platform.</p>
                <p>
                  Please <Link to="/chat/657d60c2e11554ed0ddbdc31">contact</Link> support if you believe this is an error.
                </p>
              </>
            ) : (
              <>
                <h2 style={{ color: 'red' }}>Subscription Required</h2>
                <p>Your subscription is inactive or has expired.</p>
                <p>
                  Please <Link to={`/subscription/${userId}`}>start a trial or subscribe</Link> to continue posting products.
                </p>
              </>
            )}
            <p>
              Alternatively, you can click here 👉 <Logout /> to logout and log back in.
            </p>
          </div>
        </div>
      );
    }

  return (
    <div className="product-form-container">
      <Link to={'/'}>
        <AiOutlineArrowLeft
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            fontSize: 24,
            cursor: 'pointer'
          }}
        />
      </Link>

      <h1 className="form-title">Add New Product</h1>

      <form className="product-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Product Name:</label>

          <input
            type="text"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            required
          />

          {formErrors.productName && (
            <span className="error-text">{formErrors.productName}</span>
          )}
        </div>

        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>Currency:</label>

            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              required
            >
              {currencyOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ flex: 2 }}>
            <label>Price:</label>

            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
            />

            {formErrors.price && (
              <span className="error-text">{formErrors.price}</span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>Details:</label>

          <textarea
            value={details}
            onChange={handleDetailsChange}
            required
          />

          {formErrors.details && (
            <span className="error-text">{formErrors.details}</span>
          )}
        </div>

        <div className="form-group">
          <label>Country:</label>

          <div className="country-search-container">
            <input
              type="text"
              value={countrySearch}
              onChange={(e) => {
                setCountrySearch(e.target.value);
                setShowCountryDropdown(true);
              }}
              onFocus={() => setShowCountryDropdown(true)}
              placeholder="Search for a country & Select"
              required
            />

            {showCountryDropdown && filteredCountries.length > 0 && (
              <div className="country-dropdown">
                {filteredCountries.map((country) => (
                  <div
                    key={country.name}
                    className="country-item"
                    onClick={() => handleCountrySelect(country)}
                  >
                    {country.flag} {country.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {formErrors.country && (
            <span className="error-text">{formErrors.country}</span>
          )}
        </div>

        <div className="form-group">
          <label>Location:</label>

          <input
            value={location}
            onChange={(e) => {
              const value = e.target.value;

              if (!/\d/.test(value)) {
                setLocation(value);
              }
            }}
            required
          />

          {formErrors.location && (
            <span className="error-text">{formErrors.location}</span>
          )}
        </div>

        <div className="form-group">
          <label>Category:</label>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            required
          >
            <option value="">Select a category</option>

            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          {formErrors.selectedCategory && (
            <span className="error-text">{formErrors.selectedCategory}</span>
          )}
        </div>

        <div className="form-group">
          <label>Images:</label>

          <input
            type="file"
            multiple
            onChange={handleImageChange}
            required
          />

          {formErrors.images && (
            <span className="error-text">{formErrors.images}</span>
          )}

          <div className="image-preview">
            {images.map((image, index) => (
              <img
                key={index}
                src={URL.createObjectURL(image)}
                alt="Preview"
                className="preview-image"
              />
            ))}
          </div>
        </div>

        {successMessage && (
          <p className="success-message">{successMessage}</p>
        )}

        {errorMessage && (
          <p className="error-message">{errorMessage}</p>
        )}

        {formErrors.geolocation && (
          <p className="error-text">{formErrors.geolocation}</p>
        )}

        <button
          className="submit-button"
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? 'Adding Product...' : 'Post Product'}
        </button>

        <p style={{ textAlign: 'center' }}>
          <Link
            to={`/terms&conditions/${userId}`}
            style={{ textDecoration: 'none' }}
          >
            Review our terms and conditions.
          </Link>
        </p>
      </form>
    </div>
  );
};

export default ProductForm;